import { RefereeDutiesListTable } from '~/pages/referee-duties/list/components/referee-duties-list-table';
import { RefereeDutiesForm } from '~/pages/referee-duties/components/referee-duties-form';
import { useCompetitionRegistrationListQuery } from '../../detail/registration/queries';
import { RefereeSearchSelect } from '~/pages/referee/components/referee-search-select';
import { Schema as GroupsSchema } from '../../groups/components/groups-form.schema';
import { PersonSearchSelect } from '~/pages/person/components/person-search-select';
import { RegistrationListFilter } from './components/registration-list-filter';
import { GroupsListTable } from '../../groups/components/groups-list-table';
import { GroupsForm } from '../../groups/components/groups-form';
import { CompetitionResultForm } from './components/result-form';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { useCreateGroupMutation } from '../../groups/mutations';
import { Pagination } from '~/components/Pagination/Pagination';
import { withPrivateRoute } from '~/hocs/withPrivateRoute';
import { useCompetitionResultListQuery } from './queries';
import { Skeleton } from '~/components/Skeleton/Skeleton';
import { ResultTable } from './components/result-table';
import { Heading } from '~/components/Heading/Heading';
import { useFilterForm } from '~/hooks/useFilterForm';
import { useCompetitionQuery } from '../../queries';
import { Panel } from '~/components/Panel/Panel';
import { Grid } from '~/components/Grid/Grid';
import { useParams } from '~/hooks/useParams';
import { Fragment } from 'react/jsx-runtime';
import { querySchema } from './query-schema';
import { idParamSchema } from '~/schemas';
import { toast } from 'react-toastify';
import { Result } from '~/apis/ssf';
import { useState } from 'react';

function groupResultsByCategoryAndWeight(results: Result[]) {
  return results.reduce<Record<string, Result[]>>((acc, result) => {
    const ageCategory = result.age_category.label;
    const weightClass = result.weight_class.name;
    const competitionType = result.competition_type.description;
    const key = `${competitionType}_${ageCategory}_${weightClass}`;

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(result);
    return acc;
  }, {});
}

export const Page = withPrivateRoute(() => {
  const { id } = useParams(idParamSchema.parse);
  const { data: competition } = useCompetitionQuery(id);

  const { values } = useFilterForm(querySchema.parse);
  const { data, isLoading } = useCompetitionRegistrationListQuery({
    competition: id,
    page_size: values.pageSize,
    page: values.pageIndex,
    search: values.search,
    exclude_with_result: true,
    competition_type: values.competition_type_id,
    editable: true,
  });
  const { data: results } = useCompetitionResultListQuery({ competition: id, page_size: 999 });

  const groupedResults = groupResultsByCategoryAndWeight(results?.results ?? []);

  const mutation = useCreateGroupMutation();

  async function onSubmitGroup(data: GroupsSchema) {
    await mutation.mutateAsync(data);
    toast.success('Grupp skapad');
  }

  const [licencedLifter, setLicencedLifter] = useState<[string, number] | undefined>(undefined);
  const [referee, setReferee] = useState<[string, number] | undefined>(undefined);

  return (
    <Fragment>
      {competition ? <PageLayout.Heading text={`Hantera resultat - ${competition.name}`} /> : <Skeleton />}
      <PageLayout.Section>
        <PageLayout.SubHeading>Skapa grupp</PageLayout.SubHeading>
        <Panel variant="informative" size="sm" style={{ marginBottom: 'var(--size-4)' }}>
          <p>Börja med att skapa en eller flera grupper. Du kan sedan lägga till resultat för varje grupp.</p>
        </Panel>
        <GroupsForm
          onSubmit={onSubmitGroup}
          mode="create"
          isLoading={mutation.isPending}
          competitionStartDate={competition?.start_date ?? ''}
        />
        <PageLayout.SubHeading>Befintliga grupper</PageLayout.SubHeading>
        <GroupsListTable />
      </PageLayout.Section>

      <PageLayout.Section>
        <PageLayout.SubHeading>Registrerade lyftare</PageLayout.SubHeading>
        <Panel variant="informative" size="sm" style={{ marginBottom: 'var(--size-4)' }}>
          <p>I listan finns alla registrerade lyftare utan resultat.</p>
        </Panel>
        <RegistrationListFilter />
        {data?.results.map(reg => (
          <Fragment key={`lifter-${reg.id}`}>
            <Heading as="h4">{`${reg.person.first_name} ${reg.person.last_name} (${reg.person.license_id}) - ${reg.person.club.name}`}</Heading>
            <CompetitionResultForm
              lifterId={reg.person.id}
              competitionId={id}
              isTeamComp={competition?.is_team_comp ?? false}
            />
          </Fragment>
        ))}
        <Pagination count={data?.count} />
      </PageLayout.Section>

      <PageLayout.Section>
        <PageLayout.SubHeading>Sök efter licenserad lyftare</PageLayout.SubHeading>
        <Panel variant="informative" size="sm" style={{ marginBottom: 'var(--size-4)' }}>
          <p>
            Finns inte lyftaren bland de anmälda lyftarna, så kan du addera personen genom att söka på alla licensierade
            lyftarna.
          </p>
        </Panel>
        <Grid>
          <Grid.Item cols={{ lg: 4, sm: 12 }}>
            <PersonSearchSelect
              onChange={([lifter, personId]) => {
                setLicencedLifter([lifter, Number(personId)]);
              }}
              query={{
                valid_license_on: competition?.start_date ?? undefined,
              }}
              placeholder="Sök efter licensierad lyftare..."
              value={licencedLifter}
              disabled={!data}
              name="person_id"
            />
          </Grid.Item>
        </Grid>
        {licencedLifter && (
          <>
            <Heading as="h4">{`${licencedLifter[0]}`}</Heading>
            <CompetitionResultForm
              lifterId={licencedLifter[1]}
              competitionId={id}
              isTeamComp={competition?.is_team_comp ?? false}
              onFinish={() => setLicencedLifter(undefined)}
            />
          </>
        )}
      </PageLayout.Section>

      <PageLayout.Section>
        <PageLayout.SubHeading>Registerade resultat</PageLayout.SubHeading>

        {Object.entries(groupedResults).map(([key, groupResults]) => {
          const [competitionType, ageCategory, weightClass] = key.split('_');
          return (
            <ResultTable
              key={key}
              results={groupResults}
              isLoading={isLoading}
              competitionType={competitionType}
              ageCategory={ageCategory}
              weightClass={weightClass}
            />
          );
        })}
      </PageLayout.Section>

      <PageLayout.Section>
        <PageLayout.SubHeading>Domaruppdrag</PageLayout.SubHeading>
        <Panel variant="informative" size="sm" style={{ marginBottom: 'var(--size-4)' }}>
          <p>Sök efter domare och tilldela domaruppdrag.</p>
        </Panel>
        <Grid>
          <Grid.Item cols={{ lg: 4, sm: 12 }}>
            <RefereeSearchSelect
              onChange={([referee, refereeId]) => {
                setReferee([referee, Number(refereeId)]);
              }}
              query={{
                status: ['active', 'warned'],
                ordering: 'person__first_name,person__last_name',
              }}
              placeholder="Sök efter domare..."
              name="referee"
              value={referee}
            />
          </Grid.Item>
        </Grid>
        {referee && (
          <>
            <Heading as="h4">{`${referee[0]}`}</Heading>
            <RefereeDutiesForm refereeId={referee[1]} onFinish={() => setReferee(undefined)} />
          </>
        )}
      </PageLayout.Section>

      <PageLayout.Section>
        <PageLayout.SubHeading>Registrerade domaruppdrag</PageLayout.SubHeading>
        <RefereeDutiesListTable competitionId={id} />
      </PageLayout.Section>
    </Fragment>
  );
});
